import { Amplify } from 'aws-amplify';
import { useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const ON_EVENT = /* GraphQL */ `
  subscription OnEvent($sessionId: ID!) {
    onEvent(sessionId: $sessionId) {
      sessionId
      channel
      eventType
      payload
      timestamp
      sourceUserId
    }
  }
`;

const ON_CHANNEL_EVENT = /* GraphQL */ `
  subscription OnChannelEvent($sessionId: ID!, $channel: String!) {
    onChannelEvent(sessionId: $sessionId, channel: $channel) {
      sessionId
      channel
      eventType
      payload
      timestamp
      sourceUserId
    }
  }
`;

/**
 * Subscribe to all events for a session.
 *
 * @param {string} sessionId - Session to subscribe to
 * @param {function} onEvent - Callback for each event: (event) => void
 * @param {object} options - { enabled: boolean }
 */
export function useSessionSubscription(sessionId, onEvent, options = {}) {
    const { enabled = true } = options;
    const callbackRef = useRef(onEvent);
    callbackRef.current = onEvent;

    useEffect(() => {
        if (!sessionId || !enabled) return;

        const sub = client.graphql({
            query: ON_EVENT,
            variables: { sessionId },
        }).subscribe({
            next: ({ data }) => {
                // console.log('RAW onEvent DATA:', JSON.stringify(data));
                const event = data.onEvent;
                if (event) {
                    // Parse the JSON payload
                    event.parsedPayload = JSON.parse(event.payload);
                    callbackRef.current(event);
                }
            },
            // error: (err) => console.error('Subscription error:', err),
            error: (err) => {
                console.error('Subscription error details:', JSON.stringify(err, null, 2));
            }
        });

        return () => sub.unsubscribe();
    }, [sessionId, enabled]);
}

/**
 * Subscribe to a specific channel within a session.
 *
 * @param {string} sessionId
 * @param {string} channel - e.g. 'chat', 'punchList'
 * @param {function} onEvent
 * @param {object} options
 */
export function useChannelSubscription(sessionId, channel, onEvent, options = {}) {
    const { enabled = true } = options;
    const callbackRef = useRef(onEvent);
    callbackRef.current = onEvent;

    useEffect(() => {
        if (!sessionId || !channel || !enabled) return;

        console.log('Subscribing to:', { sessionId, channel });

        const sub = client.graphql({
            query: ON_CHANNEL_EVENT,
            variables: { sessionId, channel },
        }).subscribe({
            next: ({ data }) => {
                // console.log('RAW EVENT RECEIVED:', JSON.stringify(data));

                // console.log('GraphQL config:', JSON.stringify(Amplify.getConfig().API?.GraphQL));
                const event = data.onChannelEvent;
                if (event) {
                    event.parsedPayload = JSON.parse(event.payload);
                    callbackRef.current(event);
                }
            },
            error: (err) => console.error(`Subscription error (${channel}):`, err),
        });

        console.log('Subscription created:', sub);

        return () => sub.unsubscribe();
    }, [sessionId, channel, enabled]);
}